import _ from 'lodash';
import Data from 'app-core/Data';
import Promise from 'bluebird';
import moment from 'moment';
import auth from 'src/auth';
import axios from 'axios';
import config from 'app-core/config';

const { BlobServiceClient } = require("@azure/storage-blob");

const azureSettings = {};
let sasContainers = {};
let cachedBlobs = {};
let statistics = [];
let globalLogId = null;

function setGlobalLogId(logId) {
	globalLogId = logId;
}

function clearStatistics() {
	statistics = [];
}

function addStatistics(info) {
	statistics.push({
		globalLogId, 
		info,
		logDate: new Date()
	});
}

function getStatistics() {
	return statistics;
}

function registerEvents() {
	config.eventsBus.$on('clear-cache', () => {
		if (__DEBUG__)
			console.debug('Clear azure cached configs');
			
		Object.keys(azureSettings).map(f => delete azureSettings[f]);
		sasContainers = {};
	});
}

function getSasForContainer(containerProperty) {
	if (__DEBUG__)
		console.debug('Getting Sas'/*, { cont: sasContainers[containerProperty], diff: sasContainers[containerProperty] && moment(sasContainers[containerProperty].expires).diff(moment(), 'seconds'), containerProperty }*/);
	if (!sasContainers[containerProperty] || (sasContainers[containerProperty].expires && moment(sasContainers[containerProperty].expires).diff(moment(), 'seconds') < 60)) {
		if (__DEBUG__)
			console.debug('Init new Sas data');
		sasContainers[containerProperty] = { 
			promise: Data.getExternalApi('Service/GenerateSasForCompanyContainer', `containerName=${containerProperty}`).then(r => {
				if (__DEBUG__)
					console.debug('Sas loaded'/*, { r }*/);
				sasContainers[containerProperty].expires = moment(r.ExpiresOnUtc);
				sasContainers[containerProperty].sas = r.Uri;

				return sasContainers[containerProperty];
			})
		}
	}
	return sasContainers[containerProperty].promise;
}

async function getSasForAzureContainer(containerName, expiresInMins) {
	containerName = containerName || (await Data.getAzureSettings())[getContainerNameProp()];
	expiresInMins = expiresInMins || 300;

	const cacheContainerKey = `${containerName}_${expiresInMins}`;
	if (__DEBUG__)
		console.debug('Getting Sas'/*, { cont: sasContainers[cacheContainerKey], diff: sasContainers[cacheContainerKey] && moment(sasContainers[cacheContainerKey].expires).diff(moment(), 'seconds'), containerName, cacheContainerKey }*/);
	
	if (!sasContainers[cacheContainerKey] || (sasContainers[cacheContainerKey].expires && moment(sasContainers[cacheContainerKey].expires).diff(moment(), 'seconds') < 60)) {
		if (__DEBUG__)
			console.debug('Init new Sas data');
		sasContainers[cacheContainerKey] = { 
			promise: Data.getExternalApi('Service/GetSasForAzureContainer', `containerName=${containerName}&expiresInMins=${expiresInMins}`).then(r => {
				if (__DEBUG__)
					console.debug('Sas loaded'/*, { r }*/);
				sasContainers[cacheContainerKey].expires = moment(r.ExpiresOnUtc);
				sasContainers[cacheContainerKey].sas = r.Uri;
				sasContainers[cacheContainerKey].sasKey = `${r.Uri}`.split('?')[1];
				sasContainers[cacheContainerKey].containerName = containerName;

				return sasContainers[cacheContainerKey];
			})
		}
	}
	return sasContainers[cacheContainerKey].promise;
}

function getBlobClient(containerName) {	
	return new Promise((resolve, reject) => 
		Data.getAzureSettings().then(r => {
			_.assign(azureSettings, r);
			
			if (__DEBUG__)
				console.debug('Azure settings loaded'/*, azureSettings*/);
			
			if (!r || !r.StorageAccount) {
				var message = 'There is no settings for audio data storage. Please, contact administrator.';
				config.notify(message, 'error', 10000);
				reject(message);
			}
			
			containerName = containerName || azureSettings[getContainerNameProp()];
			getSasForAzureContainer(containerName).then(s => {
				if (!s.blobClient) {
					var sas = s.sas.replace(containerName, '');
					s.blobClient = new BlobServiceClient(sas);
				}
				resolve(s.blobClient);
				return r;
			}).catch((ex) => reject(ex));

			return r;
		}).catch((ex) => reject(ex)))
		.catch((ex) => config.notify(ex, 'error', 10000));	
}

function getBlobInfo(blobName, container) {
	if (__DEBUG__)
		console.debug('Get blob', blobName);
	
	return new Promise((resolve, reject) => {
		var handleEx = (ex) => {
			if (__DEBUG__)
				console.debug('Error while getting blob');
			console.error(ex);
			resolve(null);
		};
		getBlobClient(container).then(client => {
			if (!client) return null;
			const containerClient = client.getContainerClient(container);
			const blobClient = containerClient.getBlobClient(blobName);

			blobClient.getProperties().then(props => {
				resolve({
					blobClient,
					props
				})
			});
		}).catch(ex => reject(ex));
	})
}

function deleteBlob(blobName, container) {
	if (__DEBUG__)
		console.debug('deleteBlob'/*, { blobName, container }*/);
	blobName = formatBlobName(blobName);
	return Data.getAzureSettings().then(azureSettings => {
		container = container || azureSettings[getContainerNameProp()];
		config.creativeBlobsDb.delete(`${container ? `${container}--` : ''}${blobName}`);
		return getBlobClient(container).then(client => {
			if (!client) return null;
			const containerClient = client.getContainerClient(container);
			const blockBlobClient = containerClient.getBlockBlobClient(blobName);
			return blockBlobClient.delete();
		});
	});
}

function formatBlobName(name) {
	return (name || '').replace(/[ *&^!@#$]/g, '_');
}

function getBlob(blobName, asBlob, onProgress, preventCache, container, localDbInfo, logId) {
	const info = {
		operation: `Loading blob ${blobName}`,
		type: 'DOWNLOAD',
		logId
	}
	addStatistics(info);

	const cacheKey = asBlob ? `${blobName}_blob` : `${blobName}_base64`;

	if (__DEBUG__)
		console.debug('Get blob'/*, { blobName, preventCache, sessionCache: cachedBlobs[cacheKey] }*/);

	if (!preventCache) {
		if (!cachedBlobs[cacheKey]) {
			cachedBlobs[cacheKey] = {
				progressHandlers: onProgress ? [onProgress] : []
			}
		} else {
			if (onProgress)
				cachedBlobs[cacheKey]?.progressHandlers?.push(onProgress);
			_.assign(info, {
				cached: true,
				cacheType: 'sessionCache',
				size: cachedBlobs[cacheKey]?.info?.size,
				time: 0
			});
			return cachedBlobs[cacheKey]?.promise;
		}
	}

	var promise = new Promise((resolve, reject) => {
		var handleEx = (ex) => {
			info.error = ex;
			if (__DEBUG__)
				console.debug('Error while getting blob');
			console.error(ex);
			resolve(null);
		};

		let started = new Date();
		((auth.isPublishMode() || preventCache) ? Promise.resolve(null) : config.creativeBlobsDb.get(`${container ? `${container}--` : ''}${blobName}`)).then(async localDbValue => {
			try {
				if (localDbValue) {
					info.cached = true;
					info.cacheType = 'localStorage';
					info.size = localDbValue.size;
					info.time = moment().diff(started, 'seconds');
					if (asBlob) {
						return resolve(localDbValue);
					} else {
						var value = await Data.blob2Base64(localDbValue);
						if (value?.indexOf('data:application/octet-stream') > -1) {
							value = value.replace('data:application/octet-stream', `data:${Data.getBase64ContentType(blobName)}`)
						}
						if (__DEBUG__)
							console.debug('Local db getting from'/*, { cacheKey, localDbValue, asBlob, value }*/);
						return resolve(value);
					}
				}
			} catch(ex) {
				info.cacheError = ex;
				console.error(ex);
			}
			
			getBlobClient(container).then(async client => {
				if (!client) return null;
				const containerClient = client.getContainerClient(container);
				const blobClient = containerClient.getBlobClient(blobName);
				
				blobClient.getProperties().then(props => {
					var size = props.contentLength;
					info.size = size;
					info.downloadStartDate = new Date();

					if(!preventCache) {
						cachedBlobs[cacheKey].info = info;
					}

					blobClient
					.download(void 0, void 0, { onProgress: (data) => {
						if (!preventCache) {
							cachedBlobs[cacheKey]?.progressHandlers?.map(f => f({ loaded: data.loadedBytes, total: size }));
						} else if (onProgress)
							onProgress({ loaded: data.loadedBytes, total: size });
					}})
					.then(r => {
						if (__DEBUG__)
							console.debug('Azure get blob raw response'/*, { r }*/);
						return r;
					})
					.then(r =>
						r.blobBody
							.then(b => {
								config.creativeBlobsDb.set(`${container ? `${container}--` : ''}${blobName}`, b, localDbInfo);
								if (asBlob) {
									resolve(b)
								} else {
									var fr = new FileReader;
									fr.onloadend = () => {
										var result = fr.result;
										if (result?.indexOf('data:application/octet-stream') > -1) {
											result = result.replace('data:application/octet-stream', `data:${Data.getBase64ContentType(blobName)}`)
										}
										resolve(result);
									};
									fr.readAsDataURL(b);
								}
							})
							.catch(handleEx))
					.catch(handleEx)
					.finally(() => {
						info.downloadEndDate = new Date();
						info.time = moment(info.downloadEndDate).diff(info.downloadStartDate, 'seconds');
						if (__DEBUG__)
							console.log(`Download block blob ${blobName} successfully`);
					});
					return null;
				}).catch(handleEx);
				return null;
			}).catch(handleEx);
			return null;
		});
		return null;
	}).then(r => {
		if (__DEBUG__)
			console.debug('Azure audio get blob loaded'/*, { result: r }*/);
		return r;
	});

	if (!preventCache) {
		cachedBlobs[cacheKey].promise = promise;
	}

	return promise;
}

function uploadBlob(blobName, blob, container, onProgress, localDbInfo, logId) {
	blobName = formatBlobName(blobName);

	const cacheKey = `${blobName}_blob`;
	if (cachedBlobs[cacheKey])
		cachedBlobs[cacheKey] = null;

	const info = {
		operation: `Uploading blob ${blobName}`,
		size: blob?.size,
		type: 'UPLOAD',
		logId
	}
	addStatistics(info);
	let started = new Date();
	return new Promise((resolve, reject) => {
		Data.getAzureSettings().then(azureSettings => {
			container = container || azureSettings[getContainerNameProp()];
			config.creativeBlobsDb.set(`${container ? `${container}--` : ''}${blobName}`, blob, localDbInfo);

			getBlobClient(container).then(client => {
				if (__DEBUG__)
					console.debug('Upload blob', { cont: container, blobName, blob, len: blob.size });
				if (!client) return null;
				const containerClient = client.getContainerClient(container);
				const blockBlobClient = containerClient.getBlockBlobClient(blobName);
				
				var blockSize = blob?.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;
				const uploadBlobResponse = blockBlobClient.uploadBrowserData(blob, {
					blockSize,
					onProgress: (data) => {
						const percent = parseFloat(parseFloat(data.loadedBytes / blob?.size * 100).toFixed(2));
						if (__DEBUG__)
							console.debug('On progress upload', { data, percent });
						if (onProgress)
							onProgress({ percent, total: blob?.size, loaded: data?.loadedBytes });
					}
				});
				resolve(uploadBlobResponse);
				if (__DEBUG__)
					console.log(`Upload block blob ${blobName} successfully`, uploadBlobResponse);
				return null;
			}).catch((ex) => reject(ex));
			return null;
		});
	}).finally(() => info.time = moment().diff(started, 'seconds'));
}

async function uploadBlobWithInfoResponse(blobName, blob, container, onProgress, localDbInfo) {	
	blobName = formatBlobName(blobName);

	container = container || (await Data.getAzureSettings())[getContainerNameProp()];
	return uploadBlob(blobName, blob, container, onProgress, localDbInfo).then(() => {
		var payload = {
			StorageType: 2,
			AzureAccountOid: azureSettings.StorageAccount.Oid,
			AzureContainer: container,
			AzureFileUrl: `${azureSettings.StorageAccount.BlobEndPoint}${container}/${blobName}`,
			FileName: blobName,
			FileSize: blob.size
		};
		if (__DEBUG__)
			console.debug('uploadBlobWithInfoResponse', { payload });
		return payload;
	})
}

async function getBlobDirectLink(blobName, container) {
	container = container || (await Data.getAzureSettings())[getContainerNameProp()];
	let azureSettings = await Data.getAzureSettings();
	return `${azureSettings.StorageAccount.BlobEndPoint}${container}/${blobName}`;
}

function uploadInsertionAudio(insertion, base64, customFileName, onProgress) {
	if (__DEBUG__)
		console.debug('Upload insertion audio', { insertion });
	if (!insertion || insertion?.__inMemory) return Promise.resolve(null);
	var ext = Data.getBase64AudioExtension(base64);
	var existingAudio = !!insertion.Audio && !!insertion.Audio.FileName;
	var id = insertion.Oid;
	var fileName = `${customFileName || 'insertion-audio'}---${id}--${Data.uuid()}.${ext}`;
	if (!base64) return Promise.resolve(null);	
	return new Promise((resolve, reject) => {
		return axios.get(base64, {
			responseType: 'blob'
		}).then(result => {
			var blob = result.data;
			return new Promise((res, rej) => {
				if (!existingAudio) return res(null);
				return Data.getExternalApi('Creative/RemoveAudioFromInsertion', `insertionId=${insertion.Oid}`, void 0, void 0, { silent: true }).then(r => {
					res(r);
				}).catch(() => res(null));
			}).then(() => {
				return uploadBlob(fileName, blob, void 0, onProgress, { creativeId: insertion?.CreativeId, insertionId: insertion?.Oid  }).then(r => {
					var payload = {
						ObjectId: id,
						StorageType: 2,
						AzureAccountOid: azureSettings.StorageAccount.Oid,
						AzureContainer: azureSettings[getContainerNameProp()],
						FileName: fileName,
						FileSize: blob.size
					};
					return Data.postExternalApi('Creative/SaveAzureAudioToInsertion', payload)
						.then(r => {
							if (r.Success) {
								resolve(payload);
							} else {
								reject(r.ErrorMessage);
							}
							return r;
						})
						.catch(ex => reject(ex));
				}).catch(ex => reject(ex));
			}).catch(ex => reject(ex));
		});
	}).then(r => {
		insertion.Audio = r;
		if (__DEBUG__)
			console.debug('Upload insertion audio result', { r, insertion });
		return r;
	});
}

function getBlobFileNameFromAzureInfo(info) {
	const fileName = info?.AzureContainer && info?.AzureFileUrl ? info?.AzureFileUrl.split(info?.AzureContainer + '/').slice(-1)[0] : info?.FileName;
	return `${fileName}`.replace(/^\/|\/$/g, '');
}

async function getInsertionAudio(insertion, asBlob, onProgress, preventCache, logId) {
	if (!insertion || !insertion.Audio || !insertion.Audio.FileName) return Promise.resolve(null);
	const fileName = getBlobFileNameFromAzureInfo(insertion?.Audio);
	const blob = await getBlob(fileName, asBlob, onProgress, preventCache, insertion.Audio.AzureContainer, { creativeId: insertion?.CreativeId, insertionId: insertion?.Oid }, logId);

	if (__DEBUG__)
		console.debug(`Get insertion blob ${insertion?.Oid} ${fileName}`/*, { insertion, asBlob, blob }*/);

	return blob;
}

function getFileAudioBlob(audio, asBlob, onProgress, preventCache) {
	const fileName = getBlobFileNameFromAzureInfo(audio);
	return getBlob(fileName, asBlob, onProgress, preventCache, audio?.AzureContainer);
}

function duplicateBlobLink(sourceInsertion, destInsertion) {
	if (__DEBUG__)
		console.debug('Duplicate blob link', { sourceInsertion, destInsertion });
	return Data.postExternalApi('Creative/SaveAzureAudioToInsertion', {
		...sourceInsertion.Audio,
		ObjectId: destInsertion.Oid
	})
	.then(r => {
		if (r.Success === false) {
			throw(r.ErrorMessage);
		}
		return r.Data;
	});
}

function getContainerNameProp() {
	return auth.hasCreativeApi() ? 'BaseAudioContainerName' : 'CreativeApiContainerName';
}

function uploadCreativeFinalAudio(creative, base64, customFileName, preventSaveAsFinalAudio, noWait, logId, onProgress) {
	if (__DEBUG__)
		console.debug('Upload creative final audio', { creative });
	if (!creative) return;
	return new Promise((resolve, reject) => {
		Data.getAzureSettings().then(azureSettings => {
			var containerProp = azureSettings['CreativeApiContainerName'];
			var ext = Data.getBase64AudioExtension(base64);
			var id = creative.Oid;
			var fileName = `${customFileName || 'creative-final-audio'}---${id}--${Data.uuid()}.${ext}`;
			if (!base64) return Promise.resolve(null);
			return axios.get(base64, {
				responseType: 'blob'
			}).then(result => {
				var blob = result.data;
				let upload = uploadBlob(fileName, blob, containerProp, onProgress, void 0, logId).then(() => {
					var payload = {
						ObjectId: id,
						StorageType: 2,
						AzureAccountOid: azureSettings.StorageAccount.Oid,
						AzureContainer: containerProp,
						FileName: fileName,
						FileSize: blob.size
					};
					if (preventSaveAsFinalAudio) {
						if (!noWait) {
							resolve(payload);
						}
						return payload;
					}
					return Data.postExternalApi('Creative/SaveAzureAudioAsFinalCreative', payload)
						.then(r => {
							if (r.Success) {
								if (!noWait) {
									resolve(payload);
								} else {
									return payload;
								}
							} else {
								if (!noWait) {
									reject(r.ErrorMessage);
								}
								return null;
							}
							return r;
						})
						.catch(ex => reject(ex));
				}).catch(ex => reject(ex));

				if (noWait) {
					resolve({
						upload
					});
				} else {
					return upload;
				}
			});
		}).catch(ex => reject(ex));
	}).then(r => {		
		creative.FinalAudio = r;
		return r;
	});
}

function uploadCreativeApprovalAudio(creativeApprovalId, base64, customFileName, preventSaveAsApprovalAudio) {
	if (__DEBUG__)
		console.debug('Upload creative final audio', { creativeApprovalId });
	if (!creativeApprovalId) return;
	return new Promise((resolve, reject) => {
		Data.getAzureSettings().then(azureSettings => {
			var containerProp = azureSettings['CreativeApiContainerName'];
			var ext = Data.getBase64AudioExtension(base64);
			var id = creativeApprovalId;
			var fileName = `${customFileName || 'creative-approval-audio'}---${id}--${Data.uuid()}.${ext}`;
			if (!base64) return Promise.resolve(null);
			return axios.get(base64, {
				responseType: 'blob'
			}).then(result => {
				var blob = result.data;
				return uploadBlob(fileName, blob, containerProp).then(() => {
					var payload = {
						ObjectId: id,
						StorageType: 2,
						AzureAccountOid: azureSettings.StorageAccount.Oid,
						AzureContainer: containerProp,
						FileName: fileName,
						FileSize: blob.size
					};
					if (preventSaveAsApprovalAudio) {
						resolve(payload);
						return payload;
					}
					return Data.postExternalApi('CreativeApproval/SaveAzureAudioForApproval', payload)
						.then(r => {
							if (r.Success) {
								resolve(payload);
							} else {
								reject(r.ErrorMessage);
							}
							return r;
						})
						.catch(ex => reject(ex));
				}).catch(ex => reject(ex));
			});
		}).catch(ex => reject(ex));
	}).then(r => {
		return r;
	});
}

function getCreativeFinalAudio(creative, asBlob, onProgress, preventCache) {
	if (__DEBUG__)
		console.debug('Get creative final audio blob', { creative, asBlob });

	if (!creative || !creative.FinalAudio || !creative.FinalAudio.FileName) return Promise.resolve(null);
	return getBlob(creative.FinalAudio.FileName, asBlob, onProgress, preventCache, creative.FinalAudio.AzureContainer);
}

function getReadableFileName(fileName) {
	return `${fileName}`.split('---')[0];
}

function clearCache() {
	cachedBlobs = {};
}

function postDurableFunction(url, data, opts) {
	let checkTimeout = opts?.checkTimeout || 5000;
	let finalize = null;
	let errorRes = null;
	const loaded = new Promise((res, rej) => {
		finalize = res;
		errorRes = rej;
	});

	let checkStatusUrl = null;
	let terminateUrl = null;

	let terminated = false;
	const terminate = () => {
		if (terminated) return;
		axios.get(terminateUrl).then(() => terminated = true);
	}

	let initResult = null;
	const doCheck = async () => {
		if (!checkStatusUrl) return finalize(initResult);

		await axios.get(checkStatusUrl + '&showInput=false')
			.then(({ data }) => {
				if (__DEBUG__)
					console.debug('postDurableFunction check', { url, data });
				if (['Pending', 'Running'].includes(data?.runtimeStatus)) {
					setTimeout(doCheck, checkTimeout);
				} else {
					finalize(data?.output);
					if (data?.runtimeStatus !== 'Completed') {
						config.notifyError(JSON.stringify(data?.output, void 0, '   '));
					}
				}
			})
			.catch((ex) => {
				errorRes(ex);
			});
	}
	
	axios.post(url, data, {
		headers: {
			AdthosApiUrl: auth.userInfo.user.DefaultApiUrl,
			Authorization: `Bearer ${auth.accessToken()}` 
		},
		...(opts || {})
	}).then(({ data }) => {
		initResult = data;

		if (__DEBUG__)
			console.debug('postDurableFunction', { data });
		
		checkStatusUrl = data?.statusQueryGetUri;
		terminateUrl = data?.terminatePostUri;

		doCheck();

		return data;
	}).catch((ex) => {
		errorRes(ex);
	});

	return {
		loaded: () => loaded,
		terminate
	}
}

export default {
	getBlob,
	uploadBlob,
	getBlobClient,
	uploadInsertionAudio,
	getInsertionAudio,
	azureSettings,
	sasContainers,
	registerEvents,
	duplicateBlobLink,
	uploadCreativeFinalAudio,
	uploadCreativeApprovalAudio,
	getCreativeFinalAudio,
	getReadableFileName,
	getFileAudioBlob,
	getBlobInfo,
	clearCache,
	uploadBlobWithInfoResponse,
	deleteBlob,
	getSasForAzureContainer,
	getBlobFileNameFromAzureInfo,
	postDurableFunction,
	setGlobalLogId,
	getStatistics,
	clearStatistics,
	getBlobDirectLink
}

export {
	getBlob,
	uploadBlob,
	getBlobClient,
	uploadInsertionAudio,
	getInsertionAudio,
	azureSettings,
	sasContainers,
	registerEvents,
	duplicateBlobLink,
	uploadCreativeFinalAudio,
	uploadCreativeApprovalAudio,
	getCreativeFinalAudio,
	getReadableFileName,
	getFileAudioBlob,
	getBlobInfo,
	clearCache,
	uploadBlobWithInfoResponse,
	deleteBlob,
	getSasForAzureContainer,
	getBlobFileNameFromAzureInfo,
	postDurableFunction,
	getBlobDirectLink
}